import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {addGenericErrorToast} from '@albert-io/atomic';
import getFilters from '../getFilters';
import {SortOrder, StudentAssignmentSortField} from 'gql/types';
import type {StudentAssignment, StudentAssignmentsQueryVariables} from '../StudentAssignment.types';
import {useStudentAssignmentsQuery} from '../queries/StudentAssignments.queries.generated';
import type {Value as DropdownValue} from '../FilterDropdown';

const PAGINATION_LIMIT = 25;
const TO_DO_LIMIT = 15;

export default function useAssignments(
  classroomId: string | undefined,
  dropdownValue: DropdownValue,
  searchQuery?: string
) {
  // Loaded pages are keyed by the query variables that produced them, so a
  // filter/search change evicts stale pages by derivation (no reset effect),
  // and a stale in-flight fetchMore can never leak into the fresh list.
  const [morePages, setMorePages] = useState<{
    forKey: string;
    assignments: StudentAssignment[];
    hasNextPage: boolean;
  }>({forKey: '', assignments: [], hasNextPage: true});
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const isLoadingMoreRef = useRef(false);

  const limit = dropdownValue === 'toDo' ? TO_DO_LIMIT : PAGINATION_LIMIT;

  const variables = useMemo<StudentAssignmentsQueryVariables>(
    () => ({
      filters: getFilters(classroomId, dropdownValue, searchQuery),
      limit,
      sort: [
        {
          field: StudentAssignmentSortField.DueDateProximity,
          order: SortOrder.Asc
        },
        {
          field: StudentAssignmentSortField.Name,
          order: SortOrder.Asc
        }
      ]
    }),
    [classroomId, dropdownValue, limit, searchQuery]
  );

  const variablesKey = useMemo(() => JSON.stringify(variables), [variables]);

  const {data, error, fetchMore, loading, refetch} = useStudentAssignmentsQuery({variables});
  const initialAssignments = data?.currentUser?.studentAssignments.results ?? [];
  const initialHasNextPage = data?.currentUser?.studentAssignments.hasNextPage ?? false;

  const moreAssignments = morePages.forKey === variablesKey ? morePages.assignments : [];
  const hasNextPageAfterLoadingMore =
    morePages.forKey === variablesKey ? morePages.hasNextPage : true;

  const allAssignments = [...initialAssignments, ...moreAssignments];
  const hasNextPage = initialHasNextPage && hasNextPageAfterLoadingMore;

  const offsetRef = useRef(allAssignments.length);
  useEffect(() => {
    offsetRef.current = allAssignments.length;
  });

  const exposedFetchMore = useCallback(() => {
    if (!loading && !isLoadingMoreRef.current && hasNextPage) {
      isLoadingMoreRef.current = true;
      setIsLoadingMore(true);

      fetchMore({
        variables: {
          ...variables,
          offset: offsetRef.current
        }
      })
        .then((fetchedData) => {
          const fetchedAssignments = fetchedData.data.currentUser?.studentAssignments.results ?? [];
          const fetchedHasNextPage =
            fetchedData.data.currentUser?.studentAssignments.hasNextPage ?? false;

          // File results under the key they were fetched for; if the filters
          // changed while in flight, the derived read above ignores them.
          setMorePages((current) => ({
            forKey: variablesKey,
            assignments: [
              ...(current.forKey === variablesKey ? current.assignments : []),
              ...fetchedAssignments
            ],
            hasNextPage: fetchedHasNextPage
          }));
        })
        .catch(() => {
          addGenericErrorToast();
        })
        .finally(() => {
          isLoadingMoreRef.current = false;
          setIsLoadingMore(false);
        });
    }
  }, [fetchMore, hasNextPage, loading, variables, variablesKey]);

  return {
    assignments: allAssignments,
    fetchMore: exposedFetchMore,
    isError: error !== undefined,
    isLoadingMore,
    loading,
    refetch
  };
}
